import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function Hero() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <section className="relative w-full min-h-[600px] md:min-h-[720px] flex items-center justify-center overflow-hidden">
      {/* Background image with overlay */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20minimalist%20art%20composition%20with%20soft%20flowing%20fabric%20textures%20in%20warm%20cream%20and%20sand%20tones%2C%20gentle%20gradient%20shapes%20merging%20into%20each%20other%2C%20artistic%20studio%20backdrop%20with%20delicate%20light%20play%2C%20ethereal%20and%20airy%20atmosphere%2C%20clean%20contemporary%20aesthetic%20with%20soft%20organic%20curves%2C%20no%20objects%20just%20abstract%20textures&width=1800&height=1200&seq=hero-main-bg-v3-sb&orientation=landscape"
          alt="Superbuy spreadsheet product showcase background"
          title="Superbuy Spreadsheet Product Categories 2026"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/40"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center pt-16">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background-50/90 backdrop-blur-sm text-xs font-medium text-primary-600 mb-6">
          <i className="ri-sparkling-2-line"></i>
          Updated Daily &mdash; June 2026
        </div>

        <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight mb-4">
          The Ultimate{' '}
          <span className="text-primary-300">Superbuy Spreadsheet</span>
          <br />
          Collection for{' '}
          <span className="italic">Smart Shoppers</span>
        </h1>

        <p className="text-sm md:text-base text-background-200/90 max-w-xl mx-auto mb-8 leading-relaxed">
          Discover thousands of curated products across 14 categories. Your one-stop resource
          for the best Superbuy finds, updated spreadsheets, and expert shopping guides.
        </p>

        {/* Main CTA + Category Dropdown */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-12">
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm md:text-base font-semibold hover:bg-primary-600 transition-all duration-200 shadow-lg shadow-primary-500/25 whitespace-nowrap cursor-pointer"
          >
            Superbuy Spreadsheet
            <i className="ri-arrow-right-up-line text-lg"></i>
          </a>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-2 px-6 py-3 rounded-full bg-background-50/90 backdrop-blur-sm text-foreground-800 text-sm md:text-base font-semibold hover:bg-background-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-grid-line text-primary-500"></i>
              Browse Categories
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-background-50 border border-background-200 rounded-xl shadow-xl z-50 w-64 py-2 max-h-72 overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center gap-2 px-6 py-3 rounded-full bg-foreground-800/90 backdrop-blur-sm text-background-50 text-sm md:text-base font-semibold hover:bg-foreground-950 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Buy It Now
            <i className="ri-shopping-bag-line text-lg"></i>
          </a>
        </div>

        {/* Product cards overlapping effect */}
        <div className="hidden md:flex items-end justify-center gap-0 relative h-48">
          <div className="relative w-32 h-44 z-10 opacity-80">
            <div className="w-full h-full rounded-2xl overflow-hidden bg-background-100 shadow-lg">
              <img
                src="https://readdy.ai/api/search-image?query=Closeup%20of%20elegant%20cream%20colored%20sneakers%20with%20gold%20accents%20on%20warm%20sandy%20background%2C%20soft%20natural%20lighting%2C%20premium%20product%20photography%2C%20clean%20editorial%20style&width=400&height=520&seq=hero-card-1-v3-sb&orientation=portrait"
                alt="Superbuy product showcase"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div className="relative w-40 h-52 -mx-3 z-20 scale-110">
            <div className="w-full h-full rounded-2xl overflow-hidden bg-background-100 shadow-lg">
              <img
                src="https://readdy.ai/api/search-image?query=Artfully%20folded%20cashmere%20sweater%20in%20warm%20sand%20color%20on%20minimal%20surface%2C%20soft%20diffused%20studio%20lighting%2C%20high%20end%20fashion%20editorial%20photography%2C%20neutral%20aesthetic&width=400&height=520&seq=hero-card-2-v3-sb&orientation=portrait"
                alt="Superbuy product showcase"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div className="relative w-32 h-44 z-10 opacity-80">
            <div className="w-full h-full rounded-2xl overflow-hidden bg-background-100 shadow-lg">
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20mini%20crossbody%20bag%20in%20warm%20taupe%20leather%20on%20cream%20surface%2C%20soft%20natural%20daylight%2C%20minimalist%20accessory%20photography%2C%20clean%20editorial%20composition&width=400&height=520&seq=hero-card-3-v3-sb&orientation=portrait"
                alt="Superbuy product showcase"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}